import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  LayoutDashboard, FileSpreadsheet, Landmark, Files, BarChart3, FileText, Settings as Cog,
  HelpCircle, Search, Bell, LogOut, Menu, X, Landmark as Logo, ShieldCheck,
  Users, Building2, Boxes, Wallet, PieChart, PhoneCall, ListChecks, BadgeCheck,
  Banknote as BanknoteIcon, UserCog, KeyRound, UploadCloud, Plug, Gauge, Inbox,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";

const SEARCH_GROUPS = [
  ["leads", "Leads", (r) => `/crm/leads/${r.lead_id}`, "lead_id", "customer_name"],
  ["applications", "Applications", (r) => `/crm/leads/${r.lead_id}`, "application_id", "customer_name"],
  ["payouts", "Payouts", () => "/crm/payouts", "payout_id", "application_id"],
  ["statements", "Statement analyses", (r) => `/workspace/${r.job_id}`, "job_id", "status"],
  ["banks", "Banks", () => "/crm/banks", "bank_id", "bank_name"],
  ["locations", "Bank locations", () => "/crm/banks", "location_id", "location_name"],
  ["managers", "Bank managers", () => "/crm/banks", "manager_id", "manager_name"],
  ["employees", "Employees", () => "/crm/masters", "employee_id", "employee_name"],
  ["partners", "Partners", () => "/crm/masters", "partner_id", "partner_name"],
];

const MASTER_ROLES = ["admin", "manager", "operations", "finance"];
const FINANCE_ROLES = ["admin", "finance", "manager", "operations", "partner", "employee"];

const nav = [
  { group: "Workspace" },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/image-to-excel", label: "Image → Excel", icon: FileSpreadsheet },
  { to: "/analyzer", label: "Bank Statement Analyzer", icon: Landmark },
  { to: "/documents", label: "Documents", icon: Files },
  { to: "/analyses", label: "Analyses", icon: BarChart3 },
  { to: "/reports", label: "Statement Reports", icon: FileText },
  { group: "Lending CRM" },
  { to: "/crm/dashboard", label: "Management Dashboard", icon: Gauge },
  { to: "/crm/enquiries", label: "Enquiries", icon: Inbox,
    roles: ["admin", "manager", "operations", "telecaller", "sales"] },
  { to: "/crm/pipeline", label: "Leads & Applications", icon: Users },
  { to: "/crm/followups", label: "Follow-ups", icon: PhoneCall },
  { to: "/crm/tasks", label: "Tasks", icon: ListChecks },
  { to: "/crm/approvals", label: "Approvals", icon: BadgeCheck },
  { to: "/crm/disbursement", label: "Disbursement", icon: BanknoteIcon },
  { to: "/crm/banks", label: "Banks & Locations", icon: Building2, roles: MASTER_ROLES },
  { to: "/crm/masters", label: "Masters", icon: Boxes, roles: MASTER_ROLES },
  { to: "/crm/payouts", label: "Commission & Payouts", icon: Wallet, roles: FINANCE_ROLES },
  { group: "Integrations" },
  { to: "/crm/integrations", label: "CIBIL · GST · KYC · APIs", icon: Plug, roles: MASTER_ROLES },
  { group: "Reports" },
  { to: "/crm/reports", label: "CRM Reports & MIS", icon: PieChart },
  { group: "Administration" },
  { to: "/admin/users", label: "Users", icon: UserCog, adminOnly: true },
  { to: "/admin/roles", label: "Roles & Permissions", icon: KeyRound, adminOnly: true },
  { to: "/admin/import-export", label: "Import / Export", icon: UploadCloud, roles: ["admin", "manager"] },
  { to: "/audit", label: "Audit Trail", icon: ShieldCheck, adminOnly: true },
  { to: "/settings", label: "Settings", icon: Cog },
  { to: "/help", label: "Help", icon: HelpCircle },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nvg = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [results, setResults] = useState(null);

  const loadNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/crm/notifications?limit=40");
      setNotifications(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* notifications are non-critical */ }
  }, []);

  useEffect(() => {
    loadNotifications();
    const t = setInterval(loadNotifications, 60000);
    return () => clearInterval(t);
  }, [loadNotifications]);

  const doSearch = (e) => {
    e.preventDefault();
    runSearch(q);
  };

  const runSearch = async (term) => {
    if (!term || term.trim().length < 2) return setResults(null);
    try {
      const { data } = await api.get(`/crm/search?q=${encodeURIComponent(term.trim())}`);
      setResults(data);
    } catch { setResults(null); }
  };

  return (
    <div className="min-h-screen bg-background">
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 border-r border-border bg-white transition-transform duration-200 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
             data-testid="sidebar">
        <div className="flex h-16 items-center justify-between border-b border-border px-5">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-md bg-primary text-primary-foreground">
              <Logo className="h-4 w-4" />
            </div>
            <span className="font-display text-base font-extrabold tracking-tight">GrowRupee</span>
          </Link>
          <button className="lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close-btn">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="space-y-1 overflow-y-auto p-3 pb-32" style={{ maxHeight: "calc(100vh - 8rem)" }}>
          {nav
            .filter((n) => !n.adminOnly || user?.role === "admin")
            .filter((n) => !n.roles || n.roles.includes(user?.role) || user?.role === "admin")
            .map((n) => (n.group ? (
              <p key={n.group} className="px-3 pb-1 pt-4 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                {n.group}
              </p>
            ) : (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                       data-testid={`nav-${n.to.slice(1).replace(/\//g, "-")}`}
                       className={({ isActive }) =>
                         `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors duration-200 ${
                           isActive ? "bg-secondary font-semibold text-secondary-foreground"
                                    : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}>
                <n.icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{n.label}</span>
              </NavLink>
            )))}
        </nav>
        <div className="absolute bottom-0 w-full border-t border-border bg-white p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Plan</p>
          <p className="mt-1 font-display text-sm font-bold">{user?.plan || "Starter"}</p>
          <p className="text-xs text-muted-foreground">{user?.credits ?? 0} credits remaining</p>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-white/70 px-4 backdrop-blur-xl sm:px-6">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open-btn">
            <Menu className="h-5 w-5" />
          </button>
          <form onSubmit={doSearch} className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => { setQ(e.target.value); runSearch(e.target.value); }}
                   placeholder="Search leads, applications, payouts, banks, PAN, mobile…"
                   className="bg-white pl-9" data-testid="topnav-search-input" />
            {results && (
              <div data-testid="global-search-results"
                   className="absolute left-0 top-12 max-h-96 w-full overflow-y-auto rounded-lg border border-border bg-white/95 p-2 shadow-xl backdrop-blur-xl">
                {results.total === 0 && (
                  <p className="px-2 py-4 text-center text-xs text-muted-foreground">No matches found.</p>)}
                {SEARCH_GROUPS.filter(([k]) => (results[k] || []).length).map(([k, label, to, idKey, nameKey]) => (
                  <div key={k} className="mb-1">
                    <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                      {label}</p>
                    {results[k].map((r) => (
                      <button key={r[idKey]} data-testid={`search-result-${r[idKey]}`}
                              className="block w-full rounded px-2 py-1.5 text-left text-xs hover:bg-muted"
                              onClick={() => { setResults(null); setQ(""); nvg(to(r)); }}>
                        <b>{r[nameKey] || r[idKey]}</b>
                        <span className="text-muted-foreground"> · {r[idKey]}</span>
                      </button>))}
                  </div>))}
              </div>)}
          </form>
          <div className="ml-auto flex items-center gap-1">
            <DropdownMenu onOpenChange={(o) => o && loadNotifications()}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="notifications-btn" title="Notifications"
                        className="relative">
                  <Bell className="h-4 w-4" />
                  {unread > 0 && (
                    <span data-testid="notifications-badge"
                          className="absolute right-1 top-1 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground">
                      {unread}
                    </span>)}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-96 w-80 overflow-y-auto bg-white/90 backdrop-blur-xl"
                                   data-testid="notifications-panel">
                <DropdownMenuLabel className="flex items-center justify-between">
                  <span className="text-sm font-semibold">Notifications</span>
                  <button className="text-xs font-semibold text-primary" data-testid="mark-all-read-btn"
                          onClick={async () => {
                            await api.put("/crm/notifications/read", {});
                            loadNotifications();
                          }}>Mark all read</button>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifications.length === 0 && (
                  <p className="px-3 py-6 text-center text-xs text-muted-foreground">No notifications yet.</p>)}
                {notifications.map((n) => (
                  <div key={n.notification_id} data-testid={`notification-${n.notification_id}`}
                       className={`border-b border-border/60 px-3 py-2 text-xs ${n.read ? "opacity-60" : ""}`}>
                    <p className="font-semibold">{n.title}</p>
                    <p className="text-muted-foreground">{n.message}</p>
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      {(n.created_at || "").replace("T", " ").slice(0, 16)}</p>
                  </div>))}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 px-2" data-testid="profile-menu-btn">
                  <span className="grid h-8 w-8 place-items-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                    {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
                  </span>
                  <span className="hidden text-sm font-medium sm:inline">{user?.name || user?.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white/80 backdrop-blur-xl">
                <DropdownMenuLabel>
                  <p className="text-sm font-semibold">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nvg("/settings")} data-testid="menu-settings-item">Settings</DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); nvg("/login"); }} data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
